'use client';
import React, { useState, useMemo } from 'react';
import { fetchQuery } from '@/utils/fetchQuery';
import { GET_ADDITIONAL_POSTS } from '@/lib/queries/post/related';
import { ArrowRotateRight } from 'iconsax-react';
import { CategoryNode, PostNode } from '@/types/global';
import { RelatedPostsCard } from '@/components/(blog)/post/RelatedPostsCard';
import { LoadingSpinner } from '@/components/Icons';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';

interface AdditionalPostsData {
    posts: {
        edges: { node: PostNode }[];
        pageInfo: {
            endCursor: string;
            total: number;
        };
    };
}

interface PostRelatedPostsProps {
    slug: string;
    category: CategoryNode[] | [];
    pageType?: string;
    postRelatedPosts?: {
        data: PostNode[];
        endCursor: string | null;
        totalPosts: number;
    };
}

const PostRelatedPosts: React.FC<PostRelatedPostsProps> = ({
    slug,
    category,
    postRelatedPosts,
    pageType,
}) => {
    // Helper to remove slashes for accurate comparison
    const cleanPath = (path: string) => path.replace(/\//g, '');

    const initialPosts = useMemo(() => {
        const currentSlug = cleanPath(slug);
        return postRelatedPosts?.data?.filter((post) => cleanPath(post.uri) !== currentSlug) || [];
    }, [postRelatedPosts?.data, slug]);

    const [relatedPosts, setRelatedPosts] = useState<PostNode[]>(initialPosts);
    const [loading, setLoading] = useState<boolean>(false);
    const [endCursor, setEndCursor] = useState<string | null>(postRelatedPosts?.endCursor || null);

    const [totalPosts, setTotalPosts] = useState<number>(() => {
        const originalTotal = postRelatedPosts?.totalPosts || 0;
        const currentSlug = cleanPath(slug);
        const wasCurrentPostRemoved = postRelatedPosts?.data?.some(
            (p) => cleanPath(p.uri) === currentSlug,
        );
        return wasCurrentPostRemoved ? originalTotal - 1 : originalTotal;
    });

    const loadMorePosts = async () => {
        if (!loading && endCursor && category) {
            setLoading(true);
            try {
                const postCategories: number[] = category
                    ?.map((cat) => cat?.databaseId)
                    .filter(Boolean) as number[];

                const additionalPostsData: AdditionalPostsData = await fetchQuery(
                    GET_ADDITIONAL_POSTS,
                    {
                        categoryIds: postCategories,
                        excludeIds: relatedPosts.map((post) => post.databaseId),
                        limit: 10,
                        after: endCursor,
                    },
                    10,
                );

                const currentSlug = cleanPath(slug);
                const newPosts: PostNode[] = additionalPostsData.posts.edges
                    .map((edge) => edge.node)
                    .filter((node) => cleanPath(node.uri) !== currentSlug);

                setRelatedPosts((prev) => [...prev, ...newPosts]);
                setEndCursor(additionalPostsData.posts.pageInfo.endCursor);
                // We assume the API 'total' includes the current post, so we decrement
                setTotalPosts(additionalPostsData.posts.pageInfo.total - 1);
            } catch (error) {
                console.error('Error loading more posts:', error);
            } finally {
                setLoading(false);
            }
        }
    };

    if (!category || relatedPosts.length === 0) {
        return null;
    }

    const LoadMoreButton = () =>
        relatedPosts.length < totalPosts && (
            <div
                onClick={!loading ? loadMorePosts : undefined}
                className='flex min-w-60 justify-center items-center border border-progress/10 cursor-pointer dark:border-white/10 text-progress rounded-2xl hover:bg-progress/30 transition-all dark:text-white mt-2'
            >
                {loading ? (
                    <div className='py-[14px]'>
                        <LoadingSpinner className='-ml-1 mr-3 size-5 animate-spin text-progress dark:text-white' />
                    </div>
                ) : (
                    <div className='flex gap-2 justify-center items-center rounded-2xl py-3 w-full transition-all duration-300'>
                        بیشتر
                        <ArrowRotateRight
                            size={15}
                            className='stroke-progress brightness-125 dark:stroke-white'
                        />
                    </div>
                )}
            </div>
        );

    return (
        <div className='relative'>
            {/* Header Style */}
            <div className='mb-4 flex w-full text-lg border-b border-black/30 dark:border-white/20'>
                <p className='border-b-2 pb-2 border-progress'>
                    {pageType === 'analyses' ? (
                        <>تحلیل‌های دیگر</>
                    ) : (
                        <>مطالب مرتبط با {category?.[0]?.name ?? ''}</>
                    )}
                </p>
            </div>

            {/* One list: a vertical scroller in the desktop sidebar, a horizontal row of cards on
                phones. It was two copies (one always hidden), so every article carried each related
                post twice in its HTML. */}
            <ScrollArea
                dir={'rtl'}
                className='whitespace-nowrap pb-4 lg:flex lg:max-h-[350px] lg:flex-col lg:whitespace-normal lg:pb-0'
            >
                <div className='flex w-max gap-4 lg:w-full lg:flex-col lg:gap-0'>
                    {relatedPosts.map((post, index) => (
                        <div
                            key={index}
                            className='flex w-64 flex-col gap-3 overflow-hidden rounded-lg border transition-all hover:bg-gray-50 dark:border-white/30 dark:hover:bg-white/10 lg:w-auto lg:gap-0 lg:overflow-visible lg:rounded-none lg:border-0 lg:hover:bg-transparent lg:dark:hover:bg-transparent'
                        >
                            <RelatedPostsCard
                                slug={post.uri}
                                title={post.title}
                                databaseId={post.databaseId}
                                featuredImage={post.featuredImage}
                                index={index}
                                responsiveAside
                            />
                        </div>
                    ))}
                    <div className='flex items-stretch'>
                        <LoadMoreButton />
                    </div>
                </div>
                <ScrollBar orientation='horizontal' className='lg:hidden' />
            </ScrollArea>
        </div>
    );
};

export default PostRelatedPosts;
